import React, { useState, useEffect, useRef } from 'react';

// Hardcoded Sri Lankan airports
const SRI_LANKAN_AIRPORTS = [
  {
    name: 'Bandaranaike International Airport',
    city: 'Colombo',
    code: 'CMB',
    address: 'Bandaranaike International Airport (CMB), Katunayake, Sri Lanka',
    lat: 7.1808,
    lng: 79.8841,
    placeId: 'ChIJjz5P_D5Z4joRnXZwSvpl2mg'
  },
  {
    name: 'Mattala Rajapaksa International Airport',
    city: 'Hambantota',
    code: 'HRI',
    address: 'Mattala Rajapaksa International Airport (HRI), Hambantota, Sri Lanka',
    lat: 6.2845,
    lng: 81.1240,
    placeId: 'ChIJ8VX6QGnn4ToRjP7pPMiK3hQ'
  },
  {
    name: 'Ratmalana Airport',
    city: 'Colombo',
    code: 'RML',
    address: 'Ratmalana Airport (RML), Colombo, Sri Lanka',
    lat: 6.8220,
    lng: 79.8862,
    placeId: 'ChIJTWqVK8pT4joRYoRwVVwSBAw'
  },
  {
    name: 'China Bay Airport',
    city: 'Trincomalee',
    code: 'TRR',
    address: 'China Bay Airport (TRR), Trincomalee, Sri Lanka',
    lat: 8.5385,
    lng: 81.1819,
    placeId: 'ChIJYzI_qx0O9joRoGkp6qLGvFY'
  }
];

const AirportAutocomplete = ({ 
  value, 
  onChange, 
  onSelect,
  placeholder = "Select airport...",
  label,
  className = "",
  dark = false
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [filteredAirports, setFilteredAirports] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Filter airports based on input
    if (!inputValue || inputValue.length === 0) {
      setFilteredAirports(SRI_LANKAN_AIRPORTS);
    } else {
      const searchLower = inputValue.toLowerCase();
      const filtered = SRI_LANKAN_AIRPORTS.filter(airport => 
        airport.name.toLowerCase().includes(searchLower) ||
        airport.city.toLowerCase().includes(searchLower) ||
        airport.code.toLowerCase().includes(searchLower)
      );
      setFilteredAirports(filtered);
    }
  }, [inputValue]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    setShowDropdown(true);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
    setFilteredAirports(SRI_LANKAN_AIRPORTS);
  };

  const handleSelectAirport = (airport) => {
    setInputValue(airport.address);
    onChange(airport.address);
    setShowDropdown(false);
    setHighlightedIndex(-1);

    if (onSelect) {
      onSelect({
        address: airport.address,
        lat: airport.lat,
        lng: airport.lng,
        placeId: airport.placeId,
        name: airport.name,
        code: airport.code,
        city: airport.city
      });
    }
  };

  const handleKeyDown = (e) => {
    if (!showDropdown) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredAirports.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredAirports.length) {
          handleSelectAirport(filteredAirports[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const labelClasses = dark 
    ? "block text-xs font-medium text-gray-400 mb-1" 
    : "block text-sm font-medium text-gray-700 mb-2";

  const inputClasses = dark
    ? `w-full pl-10 pr-4 py-3 border border-gray-700 rounded-lg bg-gray-900 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm ${className}`
    : `w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black transition-all text-base ${className}`;

  const dropdownClasses = dark
    ? "absolute z-50 w-full mt-2 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl max-h-64 overflow-y-auto"
    : "absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-xl max-h-64 overflow-y-auto";

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className={labelClasses}>
          {label}
        </label>
      )}
      
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          ✈️
        </div>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={inputClasses}
          autoComplete="off"
        />
      </div>

      {showDropdown && filteredAirports.length > 0 && (
        <div className={dropdownClasses}>
          {filteredAirports.map((airport, index) => (
            <div
              key={airport.placeId}
              onClick={() => handleSelectAirport(airport)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${
                dark 
                  ? (highlightedIndex === index ? 'bg-gray-800' : 'hover:bg-gray-800/50')
                  : (highlightedIndex === index ? 'bg-gray-100' : 'hover:bg-gray-50')
              } ${dark ? 'border-gray-800' : 'border-gray-100'}`}
            >
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">✈️</span>
                <div className="flex-1">
                  <div className={`font-medium text-sm flex items-center gap-2 ${dark ? 'text-white' : 'text-gray-900'}`}>
                    {airport.name}
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${dark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                      {airport.code}
                    </span>
                  </div>
                  <div className={`text-xs mt-1 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {airport.city}, Sri Lanka
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showDropdown && inputValue.length > 0 && filteredAirports.length === 0 && (
        <div className={dropdownClasses}>
          <div className="text-center text-gray-500 text-sm p-4">
            <span className="text-2xl mb-2 block">🔍</span>
            No airports found matching "{inputValue}"
          </div>
        </div>
      )}
    </div>
  );
};

export default AirportAutocomplete;